import { Disclosure } from "@headlessui/react";
import { IdentificationIcon } from "@heroicons/react/24/solid";
import { ChevronRight } from "@material-ui/icons";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { downloadSourceFile } from "../../../../actions/CarrierAgreementMgmt/Carrier";
import AddButton from "../../../../components/AddButton";
import CloseIconButton from "../../../../components/CloseIconButton";
import DownloadButton from "../../../../components/DownloadButton";
import EditOutlineButton from "../../../../components/EditOutlineButton";
import { toast } from "../../../../components/toastNotification";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";
import { PERMISSIONS } from "../../../PermissionsConstants";
import { mainSelectedTab } from "../../atoms";
import {
  businessLicenseOfficeId,
  busLicensesAddState,
  disableBLOfficeField,
} from "../atoms/atoms";
import useDownloadBLPFiles from "../hooks/useDownloadBLPFiles";
import { useGetBusLicenseAndPermitByRecordId } from "../hooks/useGetBusLicenseAndPermitByRecordId";

const getOldLicenseLabel = (
  isOldLicense: boolean | undefined | null,
): string => {
  if (isOldLicense === true) return "True";
  if (isOldLicense === false) return "False";
  return "";
};

interface busLicensesAndPermitsDetailsProps {
  rowData: any;
  rowIsExpanded: boolean;
  handleEdit: any;
  onClickCancelShow;
  setIsAdd;
  setReset;
  viewFromOffice?: any;
}

export const BusLicensesAndPermitsRowDetails: React.FC<
  busLicensesAndPermitsDetailsProps
> = ({
  rowData,
  rowIsExpanded,
  setReset,
  handleEdit,
  onClickCancelShow,
  setIsAdd,
  viewFromOffice,
}) => {
  const [busLicensePermitDetails, setBusLicensePermitDetails] = useState<any>(
    [],
  );
  const [, setDisableOfficeField] = useRecoilState(disableBLOfficeField);
  const [, setBlOfficeId] = useRecoilState(businessLicenseOfficeId);

  const {
    data: busLPData,
    isFetching,
    isSuccess,
  } = useGetBusLicenseAndPermitByRecordId(rowData?.recordId);

  useEffect(() => {
    if (isSuccess && busLPData) {
      setBusLicensePermitDetails(busLPData);
    }
  }, [isSuccess, busLPData]);

  const setAddState = useSetRecoilState(busLicensesAddState);
  const userPermissions = useRecoilValue(permissionsSelector);
  const history = useHistory();
  const [, setSelectedIndex] = useRecoilState(mainSelectedTab);
  const addPermission = PERMISSIONS.permission_buslicensesandpermits_add;
  const editPermission = PERMISSIONS.permission_buslicensesandpermits_edit;

  const [fileNameDL] = useState<string>("");
  const [enabledDownload, setEnabledDownload] = useState(false);
  useDownloadBLPFiles({
    fileName: fileNameDL,
    enabledDownloadFile: enabledDownload,
  });

  useEffect(() => {
    if (fileNameDL) {
      setEnabledDownload(true);
    }
  }, [fileNameDL]);

  const downloadFile = async (fileName) => {
    try {
      const content = await downloadSourceFile(fileName);
      if (content.status === 200) {
        const url = globalThis.URL.createObjectURL(content.data);
        const tempLink = document.createElement("a");
        tempLink.href = url;
        tempLink.setAttribute("download", fileName);
        tempLink.click();
        toast.fire("Success", "File Downloaded Successfully", "success");
      }
      if (content.status === 400) {
        toast.fire("Error Downloading File:", "File not found", "error");
      }
    } catch (error) {
      toast.fire(
        "Failed to Download File",
        "Server Error: Cannot download file due to: " + error,
        "error",
      );
    }
  };

  const goToOfficeSettings = () => {
    const url = "/officesettings/officesetting";
    history.push({
      pathname: url,
      state: {
        officeId: busLicensePermitDetails.officeId,
        action: "BusinessLicensePermit",
      },
    });
    let userTabs = [{ to: "" }];
    [...OfficeTabs].forEach((tabs) => {
      let temp = [...userPermissions].filter(
        (user) => user === tabs.permission,
      );
      if (temp.length > 0) userTabs.push({ to: tabs.to });
    });
    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };

  const goToIssuerVendor = () => {
    const url = "/officesettings/buslicensesandpermits";
    history.push({
      pathname: "/vendortab/vendor",
      state: {
        blpData: busLicensePermitDetails,
        pathname: url,
        issuer: busLicensePermitDetails.issuer,
        action: "BusinessLicensesPermits",
      },
    });
    globalThis.location.reload();
    let userTabs = [{ to: "" }];
    [...OfficeTabs].forEach((tabs) => {
      let temp = [...userPermissions].filter(
        (user) => user === tabs.permission,
      );
      if (temp.length > 0) userTabs.push({ to: tabs.to });
    });
    userTabs.shift();
    const index = userTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };

  return (
    !isFetching &&
    busLPData &&
    busLicensePermitDetails && (
      <>
        {/* ── Header ── */}
        <div className="flex mx-4 justify-between pb-1 border-secondary border-b-2">
          <div className="flex gap-2 items-center">
            <div>
              <IdentificationIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
            </div>
            <div className="flex flex-col leading tight">
              <h3 className="text-xs font-medium text-gray-500">
                Business License and Permit
              </h3>
              <h1 className="mr-auto font-bold text-primary text-md uppercase">
                <span className="pr-2">
                  {"LP-" +
                    String(busLicensePermitDetails.recordId)?.padStart(4, "0")}
                </span>
              </h1>
            </div>
          </div>
          <div className="flex justify-between">
            <div className="flex items-center gap-2">
              {userPermissions.includes(addPermission) && (
                <AddButton
                  className={"opacity-100"}
                  disabled={false}
                  buttonText="Add"
                  onClick={() => {
                    setIsAdd(true);
                    setAddState(true);
                    setDisableOfficeField(false);
                    setBlOfficeId(null);
                  }}
                />
              )}
              {userPermissions.includes(editPermission) && (
                <EditOutlineButton
                  buttonText="Edit"
                  className={""}
                  disabled={false}
                  onClick={() => {
                    handleEdit(busLicensePermitDetails);
                  }}
                />
              )}
            </div>
            <CloseIconButton onClick={() => onClickCancelShow()} />
          </div>
        </div>

        {/* ── Main Section ── */}
        <div className="bg-bgDB">
          {rowIsExpanded && (
            <div className="mx-4 mt-4 mb-2 bg-white rounded-lg box-shadow">
              <div className="text-quinary font-bold font-sans py-2 px-4">
                Main Section
              </div>
              <div className="">
                <div className="grid grid-cols-1 sm:grid-cols-2">
                  <div className="p-2 divide-y">
                    <div className="grid grid-cols-3 p-2">
                      <span className="col-span-1 mx-4 expandedLabel fontExpand">
                        Record Number:
                      </span>
                      <div className="col-span-2 expandedData fontExpand">
                        <span className="pr-2">
                          {"LP-" +
                            String(busLicensePermitDetails.recordId)?.padStart(
                              4,
                              "0",
                            )}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="hidden sm:block" />
                  <div className="p-2 divide-y">
                    <div className="grid grid-cols-3 p-2">
                      <span className="col-span-1 mx-4 expandedLabel fontExpand">
                        Office Name:
                      </span>
                      <div className="col-span-2 expandedData fontExpand">
                        <span className="pr-2">
                          <button
                            type="button"
                            className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer text-left"
                            onClick={goToOfficeSettings}
                          >
                            {busLicensePermitDetails.fromOfficeName}
                          </button>
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-2 divide-y">
                    <div className="grid grid-cols-3 p-2">
                      <span className="col-span-1 mx-4 expandedLabel fontExpand">
                        Store Number:
                      </span>
                      <div className="col-span-2 expandedData fontExpand">
                        <span className="pr-2">
                          {busLicensePermitDetails.storeNumber}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Licenses and Permits Detail Section ── */}
        <div className="bg-bgDB">
          {rowIsExpanded && (
            <Disclosure defaultOpen>
              {({ open }) => (
                <div className="mx-4 mb-2 bg-white rounded-lg box-shadow">
                  <Disclosure.Button className="disclosureBtn ml-2">
                    <ChevronRight
                      className={`${
                        open ? "transform rotate-90" : ""
                      } text-gray-500 w-5 h-5`}
                    />
                    <div className="text-quinary font-bold font-sans py-2 px-1">
                      Licenses and Permits Detail Section
                    </div>
                  </Disclosure.Button>
                  <Disclosure.Panel className="text-xxs">
                    <div className="grid grid-cols-1 sm:grid-cols-2">
                      {/* Left column — same order as Edit */}
                      <div className="p-2 divide-y">
                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Issuer:
                          </span>
                          <button
                            type="button"
                            className="blueAndBold underline expandedData fontExpand text-xs cursor-pointer text-left"
                            onClick={goToIssuerVendor}
                          >
                            {busLicensePermitDetails.issuerName}
                          </button>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Issuer Phone:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.issuerPhone}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Type:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.busLicensePermitTypeName}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Issued Date:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.issuedDate
                              ? new Date(
                                  busLicensePermitDetails.issuedDate,
                                ).toLocaleDateString()
                              : ""}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Renewed Date:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.renewedDate
                              ? new Date(
                                  busLicensePermitDetails.renewedDate,
                                ).toLocaleDateString()
                              : ""}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Renewal Status:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.renewalStatus}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Expiration Date:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.expirationDate
                              ? new Date(
                                  busLicensePermitDetails.expirationDate,
                                ).toLocaleDateString()
                              : ""}
                          </div>
                        </div>
                      </div>

                      {/* Right column — same order as Edit */}
                      <div className="p-2 divide-y">
                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            License Status:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.licenseStatus}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            License Required:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.isLicenseRequired === true
                              ? "True"
                              : "False"}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            License/Permit Number:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {busLicensePermitDetails.licenseOrPermitNumber}
                          </div>
                        </div>

                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Old License:
                          </span>
                          <div className="col-span-2 expandedData fontExpand">
                            {getOldLicenseLabel(
                              busLicensePermitDetails.isOldLicense,
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
          )}
        </div>

        {/* ── Notes Section ── */}
        <div className="bg-bgDB">
          {rowIsExpanded && (
            <Disclosure defaultOpen>
              {({ open }) => (
                <div className="mx-4 mb-2 bg-white rounded-lg box-shadow">
                  <Disclosure.Button className="disclosureBtn ml-2">
                    <ChevronRight
                      className={`${
                        open ? "transform rotate-90" : ""
                      } text-gray-500 w-5 h-5`}
                    />
                    <div className="text-quinary font-bold font-sans py-2 px-1">
                      Notes Section
                    </div>
                  </Disclosure.Button>
                  <Disclosure.Panel className="text-xxs">
                    <div className="">
                      <div className="grid grid-cols-1 sm:grid-cols-2">
                        <div className="p-2 divide-y">
                          <div className="grid grid-cols-3 p-2">
                            <span className="col-span-1 mx-4 expandedLabel fontExpand">
                              Notes:
                            </span>
                            <div className="col-span-2 expandedData fontExpand">
                              {busLicensePermitDetails.notes}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
          )}
        </div>

        {/* ── Attachments Section ── */}
        <div className="bg-bgDB">
          {rowIsExpanded && (
            <Disclosure defaultOpen>
              {({ open }) => (
                <div className="mx-4 mb-2 bg-white rounded-lg box-shadow">
                  <Disclosure.Button className="disclosureBtn ml-2">
                    <ChevronRight
                      className={`${
                        open ? "transform rotate-90" : ""
                      } text-gray-500 w-5 h-5`}
                    />
                    <div className="text-quinary font-bold font-sans py-2 px-1">
                      Attachments Section
                    </div>
                  </Disclosure.Button>
                  <Disclosure.Panel className="text-xxs">
                    <div className="grid grid-cols-1 sm:grid-cols-2">
                      <div className="p-2 divide-y">
                        <div className="grid grid-cols-3 p-2">
                          <span className="col-span-1 mx-4 expandedLabel fontExpand">
                            Attachments:
                          </span>
                          <div className="col-span-2 expandedData fontExpand divide-y space-y-2">
                            {busLicensePermitDetails?.fileNames?.map((file) => (
                              <div
                                key={file}
                                className="grid grid-cols-3 items-center space-x-2"
                              >
                                <div className="col-span-2">
                                  {file.businessLicensePermitFileName}
                                </div>
                                <div>
                                  <DownloadButton
                                    buttonValue="Download"
                                    onClick={() =>
                                      downloadFile(
                                        file.businessLicensePermitFileName,
                                      )
                                    }
                                    className={undefined}
                                    disabled={false}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
          )}
        </div>

        {/* ── System Information ── */}
        <div>
          <div className="">
            {rowIsExpanded && (
              <Disclosure defaultOpen>
                {({ open }) => (
                  <div className="mx-4 mb-2 bg-white rounded-lg box-shadow">
                    <Disclosure.Button className="disclosureBtn ml-2">
                      <ChevronRight
                        className={`${
                          open ? "transform rotate-90" : ""
                        } text-gray-500 w-5 h-5`}
                      />
                      <span className="disclosureLabel ml-2">
                        System Information
                      </span>
                    </Disclosure.Button>
                    <Disclosure.Panel className="text-xxs">
                      <div className="grid grid-cols-1 md:grid-cols-1">
                        <div className="grid grid-cols-1 sm:grid-cols-2">
                          <div className="p-2 divide-y">
                            <div className="grid grid-cols-3 p-2">
                              <span className="col-span-1 mx-4 expandedLabel fontExpand">
                                Last Modified By:
                              </span>
                              <div className="col-span-2 expandedData fontExpand">
                                <span className="pr-2">
                                  {busLicensePermitDetails.modifiedBy}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="p-2 divide-y">
                            <div className="grid grid-cols-3 p-2">
                              <span className="col-span-1 mx-4 expandedLabel fontExpand">
                                Last Modified Date:
                              </span>
                              <div className="col-span-2 expandedData fontExpand">
                                <span className="pr-2">
                                  {busLicensePermitDetails.modifiedDate &&
                                    new Date(
                                      busLicensePermitDetails.modifiedDate,
                                    ).toLocaleDateString() +
                                      " " +
                                      new Date(
                                        busLicensePermitDetails.modifiedDate,
                                      ).toLocaleTimeString(navigator.language, {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </Disclosure.Panel>
                  </div>
                )}
              </Disclosure>
            )}
          </div>
        </div>
      </>
    )
  );
};